import {
  UseMutationOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

import axiosInstance, { CustomAxiosError } from '../../../utils/axios';
import { IErrorResponse, QueryKeys } from '../../querys/querys.enum';
import { MutationKeys } from '../../mutation/mutation.enum';
import {
  IOrganizationUpdateResponse,
  Organization,
  UpdateOrganizationVariables,
} from '../../../models/Interface/Organization.interface';

const useUpdateOrganizationReq = async (
  orgId: string,
  data: Partial<Organization>
) => {
  try {
    const response = await axiosInstance.put<IOrganizationUpdateResponse>(
      `organizations/${orgId}`,
      data
    );
    return response.data;
  } catch (error) {
    console.error("Error in useUpdateOrganizationReq:", error);
    throw error;
  }
};

const useUpdateOrganization = (
  options?: Partial<
    UseMutationOptions<
      IOrganizationUpdateResponse,
      AxiosError<IErrorResponse>,
      UpdateOrganizationVariables
    >
  >
) => {
  const queryClient = useQueryClient();
  
  return useMutation<
    IOrganizationUpdateResponse,
    AxiosError<IErrorResponse>,
    UpdateOrganizationVariables
  >({
    mutationKey: [MutationKeys.UPDATE_ORGANIZATION],
    mutationFn: async ({ orgId, data }) => {
      try {
        return await useUpdateOrganizationReq(orgId, { ...data });
      } catch (error) {
        console.error("Error in useUpdateOrganization mutationFn:", error);
        throw error;
      }
    },
    retry: 0, // Don't retry on failure
    ...options,
    onSuccess: (data, variables, cx) => {
      try {
        // Update cache data if needed
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.ORGANIZATION, { orgId: variables.orgId }],
        });
        
        // Show success message
        alert('Organization updated successfully...');
        
        // Call user-provided onSuccess handler
        if (options?.onSuccess) {
          options.onSuccess(data, variables, cx);
        }
      } catch (successError) {
        console.error("Error in onSuccess handler:", successError);
      }
    },
    onError: (error: CustomAxiosError, variables, context) => {
      try {
        // Log the error
        console.error("Update Organization error:", error?.response?.data);
        
        // Show error message
        alert(error?.response?.data?.message || "An error occurred during update");
        
        // Call user-provided onError handler
        if (options?.onError) {
          options.onError(
            error as AxiosError<IErrorResponse>,
            variables,
            context
          );
        }
      } catch (errorHandlingError) {
        console.error("Error in onError handler:", errorHandlingError);
      }
    },
  });
};

export default useUpdateOrganization;
